<template>
  <Modal
    content-class="full-height column no-wrap overflow-hidden"
    :full-height="isMobile"
    :header-class="[]"
    :modal-style="modalStyle"
    persistent
    @close="closeModal"
  >
    <template #header>
      <div
        class="tw:flex tw:items-center tw:gap-2 tw:px-4 tw:py-3"
        data-testid="header"
      >
        <div class="tw:text-lg tw:font-bold">
          {{ modalTitle }}
        </div>

        <Tag
          v-if="filtersTotal"
          color="secondary"
          data-testid="total-filters"
          hide-cursor
          hide-remove-button
          :label="filtersTotal"
          rounded
        />
      </div>
    </template>
    <div class="tw:flex tw:h-full tw:flex-1">
      <div
        v-if="!isMobile && !isAdminMode"
        class="tw:relative tw:flex tw:w-[284px] tw:shrink-0 tw:flex-col tw:overflow-auto"
        data-testid="sidebar"
      >
        <Transition name="slide-left">
          <div
            v-if="activeMenuItem === 'sources'"
            class="tw:absolute tw:z-10 tw:w-[268px] tw:bg-white tw:py-4 tw:pl-4"
          >
            <div class="tw:mb-2 tw:flex tw:flex-col">
              <StreamFiltersMenuButton
                data-testid="go-back"
                icon="left-arrow"
                :label="getLocaleText('modals.advanced_filters_modal.go_back')"
                @click="activeMenuItem = previousMenuItem"
              />
            </div>
            <div class="tw:mb-2 tw:flex">
              <Tabs
                v-model="sourceTab"
                class="tw:w-full tw:font-bold"
                data-testid="source-tabs"
                type="toggles"
              >
                <Tab
                  class="tw:flex-1"
                  :label="
                    getLocaleText('modals.advanced_filters_modal.include')
                  "
                  name="include"
                  new-design
                  type="toggle"
                />
                <Tab
                  class="tw:flex-1"
                  :label="
                    getLocaleText('modals.advanced_filters_modal.exclude')
                  "
                  name="exclude"
                  new-design
                  type="toggle"
                />
              </Tabs>
            </div>
            <div class="tw:mb-2 tw:text-sm tw:text-denim-700">
              {{
                getLocaleText("modals.advanced_filters_modal.source_switching")
              }}
            </div>
            <SourceFilters
              data-testid="source-filters"
              :editing-stream="editingStream"
              :enabled-media="enabledMedia"
              :read-only="readOnly"
              :stream="stream"
              @edit-stream="editStream"
            />
          </div>
          <div
            v-else
            class="tw:absolute tw:flex tw:w-[268px] tw:flex-col tw:gap-px tw:bg-white tw:py-4 tw:pl-4"
          >
            <template
              v-for="tab in menuItems"
              :key="tab.filter"
            >
              <StreamFiltersMenuButton
                v-if="!tab.hidden"
                :key="tab.filter"
                :active="activeMenuItem === tab.filter"
                :arrow="tab.arrow"
                :data-testid="`menu-${tab.filter}`"
                :disabled="tab.disabled"
                :filter-count="tab.filterCount"
                :icon="tab.icon"
                :label="tab.label"
                :tooltip="tab.tooltip"
                @click="onClickMenuItem(tab)"
              />
            </template>
          </div>
        </Transition>
      </div>
      <div class="tw:w-px tw:bg-denim-200" />
      <StreamFiltersKeywords
        v-if="activeMenuItem === 'keywords'"
        v-model:boolean-query="localFilters.booleanQuery"
        v-model:excluded-keywords="localFilters.excludedKeywords"
        v-model:keywords="localFilters.keywords"
        v-model:simple-boolean="localFilters.simpleBoolean"
        :show-keywords="showKeywordFilters"
      />
      <SourceListSelection
        v-if="activeMenuItem === 'sources'"
        v-model:selected-sources="selectedSources"
        v-model:view="sourceSelectionView"
        data-testid="source-list-selection"
        :editing-stream="editingStream"
        :read-only="readOnly"
        :stream="stream"
        @edit-stream="editStream"
      />
      <StreamFiltersMedia
        v-if="activeMenuItem === 'media'"
        v-model:category-keys="localFilters.categoryKeys"
        v-model:domain-authority="localFilters.domainAuthorityRanges"
        v-model:include-international-plus="
          localFilters.includeInternationalPlus
        "
        v-model:include-online-cald-sources="
          localFilters.includeOnlineCaldSources
        "
        v-model:include-print-cald-sources="
          localFilters.includePrintCaldSources
        "
        v-model:location-keys="localFilters.locationKeys"
        v-model:media="localFilters.media"
        v-model:refresh-media="refreshMedia"
        v-model:selected-social-content="localFilters.selectedSocialContent"
        v-model:selected-tv-content="localFilters.selectedTvContent"
        v-model:social-impact-threshold="localFilters.socialImpactThreshold"
        v-model:social-only-verified="localFilters.socialOnlyVerified"
        v-model:tiers="localTiers"
        :stream="stream"
      />
      <StreamFiltersContent
        v-if="activeMenuItem === 'content'"
        v-model:authors="localFilters.authors"
        v-model:excluded-authors="localFilters.excludedAuthors"
        v-model:excluded-language-codes="localFilters.excludedLanguageCodes"
        v-model:excluded-license="localFilters.excludedLicense"
        v-model:excluded-tags="localFilters.excludedTags"
        v-model:language-codes="localFilters.languageCodes"
        v-model:page-numbers="localFilters.pageNumbers"
        v-model:page-numbers-enabled="localFilters.pageNumbersEnabled"
        v-model:tags="localFilters.tags"
        v-model:word-count="localFilters.wordCount"
        v-model:word-count-enabled="localFilters.wordCountEnabled"
        :stream="stream"
      />
      <div v-if="activeMenuItem === 'date-range'">
        <InputDate
          v-model:range="localFilters.range"
          close-on-update
          date-style="medium"
          limit-to-past-year
          range-selection="custom"
          show-date-input
          show-range-options
          show-time-input
          time-style="short"
          @update:range="localFilters.dateRangeEnabled = true"
        />
      </div>
      <StreamFiltersMetrics
        v-if="activeMenuItem === 'metrics'"
        v-model:advertisement-rate="localFilters.advertisementRate"
        v-model:aggregate-emotionality="localFilters.aggregateEmotionality"
        v-model:aggregate-fakeness="localFilters.aggregateFakeness"
        v-model:aggregate-harmful="localFilters.aggregateHarmful"
        v-model:aggregate-spam="localFilters.aggregateSpam"
        v-model:audience="localFilters.audience"
        v-model:desktop-monthly-visits="localFilters.desktopMonthlyVisits"
        v-model:impact-score="localFilters.impactScoreRanges"
        v-model:mobile-monthly-visits="localFilters.mobileMonthlyVisits"
        v-model:react-score="localFilters.reactScore"
        v-model:react-score-average-selected="
          localFilters.reactScoreAverageSelected
        "
        v-model:sentiment="localFilters.sentiment"
        v-model:sentiment-enabled="localFilters.sentimentEnabled"
        v-model:unrated-sentiment="localFilters.unratedSentiment"
        :stream="stream"
      />
      <StreamFiltersOther
        v-if="activeMenuItem === 'other'"
        v-model:external-items-enabled="localFilters.externalItemsEnabled"
        v-model:only-show-external-items="localFilters.onlyShowExternalItems"
      />
      <StreamFiltersReports
        v-if="activeMenuItem === 'reports'"
        v-model:categories="localFilters.categories"
        v-model:included-mentions-enabled="localFilters.includedMentionsEnabled"
        v-model:last-saved-enabled="localFilters.lastSavedEnabled"
        v-model:sort-options="localFilters.sortOptions"
        v-model:tiers="localFilters.tiers"
        :report-updated="organisationReportUpdated"
        :stream="stream"
      />
    </div>

    <template #buttons>
      <div class="tw:flex tw:flex-1 tw:justify-between">
        <div>
          <BaseButton
            v-if="showClearButton"
            data-testid="clear-button"
            :label="clearLabel"
            new-design
            @click="clear"
          />
        </div>

        <div>
          <BaseButton
            data-testid="cancel-button"
            :label="getLocaleText('global.cancel')"
            new-design
            @click="closeModal"
          />
          <BaseButton
            color="primary"
            data-testid="confirm-button"
            :label="getLocaleText('global.confirm')"
            new-design
            @click="confirm"
          />
        </div>
      </div>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import { cloneDeep } from "lodash";
import { type CSSProperties, computed, inject, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";

import { track } from "shared/boot/analytics";
import { getLocaleText } from "shared/boot/i18n";
import { BaseButton, InputDate, Tab, Tabs, Tag } from "shared/components/base";
import Modal from "shared/components/core/modals/Modal.vue";
import SourceFilters from "shared/components/sources/SourceFilters.vue";
import SourceListSelection, {
  type SourceListSelectionView,
} from "shared/components/sources/SourceListSelection.vue";
import StreamFiltersContent from "shared/components/streams/StreamFilters/StreamFiltersContent.vue";
import StreamFiltersKeywords from "shared/components/streams/StreamFilters/StreamFiltersKeywords.vue";
import StreamFiltersMedia, {
  type StreamTiers,
} from "shared/components/streams/StreamFilters/StreamFiltersMedia.vue";
import StreamFiltersMenuButton from "shared/components/streams/StreamFilters/StreamFiltersMenuButton.vue";
import StreamFiltersMetrics from "shared/components/streams/StreamFilters/StreamFiltersMetrics.vue";
import StreamFiltersOther from "shared/components/streams/StreamFilters/StreamFiltersOther.vue";
import StreamFiltersReports from "shared/components/streams/StreamFilters/StreamFiltersReports.vue";
import useSourceFilters from "shared/composables/useSourceFilters";
import useStreamFiltering from "shared/composables/useStreamFiltering";
import { type MediumName } from "shared/helpers/media";
import { safePush } from "shared/helpers/routing";
import StreamFilters, {
  type InitialStreamFilters,
} from "shared/helpers/StreamFilters";
import type { Nullable, Stream } from "shared/types";

export type AdvancedFiltersModalFilter =
  | "content"
  | "date-range"
  | "keywords"
  | "media"
  | "metrics"
  | "other"
  | "reports"
  | "sources";

type AdvancedFiltersModalSourceTab = "include" | "exclude";

interface AdvancedFiltersModalMenuItem {
  arrow?: boolean;
  filter: AdvancedFiltersModalFilter;
  filterCount?: number;
  hidden?: boolean;
  icon: string;
  label: string;
  disabled?: boolean;
  tooltip?: string;
}

interface AdvancedFiltersModalProps {
  editingStream?: boolean;
  enabledMedia?: MediumName[];
  filter?: AdvancedFiltersModalFilter;
  filters: StreamFilters;
  initialFilters?: Nullable<InitialStreamFilters>;
  initialSourceTab?: AdvancedFiltersModalSourceTab;
  initialSourceView?: SourceListSelectionView;
  organisationReport?: boolean;
  organisationReportUpdated?: boolean;
  readOnly?: boolean;
  scheduledReport?: boolean;
  stream: Stream;
  streamReport?: boolean;
  tiers?: StreamTiers;
}

const { totalFilters: totalSourceFilters, clearFilters: clearSourceFilters } =
  useSourceFilters();

const router = useRouter();
const safeRouterPush = safePush({ router });
const route = useRoute();

const props = defineProps<AdvancedFiltersModalProps>();

const emit = defineEmits<{
  close: [];
  filters: [StreamFilters];
  tiers: [StreamTiers];
}>();

const isMobile = inject<boolean>("isMobile");
const isAdminMode = inject<boolean>("isAdminMode");

const modalTitle = computed<string>(() =>
  getLocaleText("modals.advanced_filters_modal.title")
);

const modalStyle = computed<CSSProperties>(() => {
  if (isMobile) {
    return {};
  }

  return { maxWidth: "860px", height: "640px" };
});

const localFilters = ref<StreamFilters>(props.filters.clone());

const localEnabledMedia = computed<MediumName[]>(() => {
  if (localFilters.value.media.length) {
    return localFilters.value.media;
  }

  return props.stream.enabled_media;
});

const { filtersTotal, filtersSelected } = useStreamFiltering({
  stream: props.stream,
  filters: localFilters,
});

const activeMenuItem = ref<AdvancedFiltersModalFilter>(
  props.filter || (isMobile || isAdminMode ? "media" : "keywords")
);

const previousMenuItem = ref<AdvancedFiltersModalFilter>(
  activeMenuItem.value === "sources" ? "keywords" : activeMenuItem.value
);

const disableSourcesMenuItem = computed<boolean>(() =>
  Boolean(
    localEnabledMedia.value.length &&
      localEnabledMedia.value.every((item) => item === "Social")
  )
);

const menuItems = computed<AdvancedFiltersModalMenuItem[]>(() => [
  {
    label: getLocaleText("modals.advanced_filters_modal.keywords_label"),
    icon: "keyboard",
    filter: "keywords",
    filterCount: filtersSelected.value.keywords,
    hidden: props.editingStream && !props.stream.id,
  },
  {
    label: getLocaleText("modals.advanced_filters_modal.media_types_label"),
    icon: "media",
    filter: "media",
    filterCount: filtersSelected.value.media,
    hidden: props.scheduledReport,
  },
  {
    label: getLocaleText("modals.advanced_filters_modal.sources_label"),
    icon: "sources",
    filter: "sources",
    filterCount: filtersSelected.value.sources,
    arrow: true,
    disabled: disableSourcesMenuItem.value,
    tooltip: disableSourcesMenuItem.value
      ? getLocaleText("modals.advanced_filters_modal.sources_disabled_tooltip")
      : "",
  },
  {
    label: getLocaleText("modals.advanced_filters_modal.content_label"),
    icon: "content",
    filter: "content",
    filterCount: filtersSelected.value.content,
  },
  {
    label: getLocaleText("modals.advanced_filters_modal.date_range_label"),
    icon: "calendar",
    filter: "date-range",
    filterCount: filtersSelected.value.dateRange,
    hidden: props.scheduledReport || props.editingStream,
  },
  {
    label: getLocaleText("modals.advanced_filters_modal.metrics_label"),
    icon: "metrics",
    filter: "metrics",
    filterCount: filtersSelected.value.metrics,
  },
  {
    label: getLocaleText("modals.advanced_filters_modal.other_label"),
    icon: "list",
    filter: "other",
    filterCount: filtersSelected.value.other,
  },
  {
    label: getLocaleText("modals.advanced_filters_modal.reports_label"),
    icon: "reports",
    filter: "reports",
    hidden: !props.organisationReport,
    filterCount: filtersSelected.value.reports,
  },
]);

function onClickMenuItem(menuItem: AdvancedFiltersModalMenuItem) {
  previousMenuItem.value = activeMenuItem.value;

  activeMenuItem.value = menuItem.filter;
}

const sourceSelectionView = ref<SourceListSelectionView>(
  props.initialSourceView || "results"
);

const sourceTab = ref<AdvancedFiltersModalSourceTab>(
  props.initialSourceTab ||
    (localFilters.value.excludedSources.length ? "exclude" : "include")
);

const refreshMedia = ref<boolean>(false);

const selectedSources = computed({
  get() {
    if (sourceTab.value === "exclude") {
      return localFilters.value.excludedSources;
    }

    return localFilters.value.sources;
  },
  set(value) {
    localFilters.value.sources = [];
    localFilters.value.excludedSources = [];

    if (sourceTab.value === "exclude") {
      localFilters.value.excludedSources = value;
    } else {
      localFilters.value.sources = value;
    }
  },
});

const clearLabel = computed(() => {
  if (activeMenuItem.value === "sources") {
    if (sourceSelectionView.value === "results") {
      return getLocaleText(
        "modals.advanced_filters_modal.clear_source_filters"
      );
    }

    return getLocaleText(
      "modals.advanced_filters_modal.clear_source_selections"
    );
  }

  return getLocaleText("modals.advanced_filters_modal.clear_all_filters");
});

const showClearButton = computed<boolean>(() => {
  if (activeMenuItem.value === "sources") {
    if (sourceSelectionView.value === "results") {
      return Boolean(totalSourceFilters.value);
    }

    return Boolean(selectedSources.value.length);
  }

  return true;
});

const showKeywordFilters = computed<boolean>(
  () => props.organisationReport || props.scheduledReport || props.streamReport
);

const localTiers = ref<Nullable<StreamTiers>>(props.tiers || null);

function clear() {
  if (activeMenuItem.value === "sources") {
    if (sourceSelectionView.value === "results") {
      clearSourceFilters();

      return;
    }

    clearSources();

    return;
  }

  refreshMedia.value = true;

  localFilters.value = new StreamFilters(
    props.stream,
    cloneDeep(props.initialFilters) ?? {}
  );

  clearSourceFilters();
  sourceTab.value = "include";
}

function clearSources() {
  clearSourceFilters();
  localFilters.value.sources = [];
  localFilters.value.excludedSources = [];
}

function confirm(): void {
  const filters = localFilters.value.appliedFilters();

  if (filters.length) {
    track("Advanced Filters applied", {
      filters,
      route: route.name,
    });
  }

  emit("filters", localFilters.value);
  if (localTiers.value) emit("tiers", localTiers.value);
  closeModal();
}

function closeModal(): void {
  clearSourceFilters();
  emit("close");
}

function editStream(): void {
  if (props.stream?.id) {
    safeRouterPush({
      name: "mention-stream-edit",
      params: {
        streamSlug: props.stream!.slug,
        groupSlug: props.stream!.group?.slug,
      },
    });
  }

  closeModal();
}

watch(sourceTab, () => {
  sourceSelectionView.value = selectedSources.value.length
    ? "selected"
    : "results";

  clearSources();
});
</script>

<style lang="scss" scoped>
@import "shared/css/utils/transitions/slideLeft";
</style>
